/**
 * `<JoinRoute>` — `/join?token=<invitation>`
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/functional-design/frontend-components.md § <JoinWorkspacePage>`
 * + FD Flow 6 (invitee accepts), updated for the cookies-removed auth-redesign.
 *
 * Two-step flow:
 *   1. `useQuery` previews the invitation (`/api/auth/join/preview?token=…`)
 *      so the invitee sees who invited them and to which workspace + role.
 *   2. The Accept button POSTs `/api/auth/join`. The API responds with
 *      either `{ pending: true }` (first-time user — magic link sent) or
 *      `{ token, workspace, role }` (existing user — session JWT issued
 *      in the response body). `authClient.acceptInvitation` captures the
 *      `token` into `localStorage` for us; this component just navigates.
 *
 * Invalid / expired / consumed tokens surface as a destructive Alert
 * via the central error mapping; the user is told to ask the inviter
 * for a fresh link.
 */

import { authClient, useAuth, type AuthClientError } from "@cometchat-ai/auth/client";
import { Alert, AlertDescription, Button, Spinner, toast } from "@cometchat-ai/ui-dashboard";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2 } from "../components/icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";

import { AuthLayout } from "../layouts/auth-layout";
import { formatApiError } from "../lib/format-error";

export function JoinRoute() {
  const { t } = useTranslation("auth");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = params.get("token") ?? "";

  const preview = useQuery({
    queryKey: ["invitation-preview", token],
    queryFn: () => authClient.previewInvitation({ token }),
    enabled: token.length > 0,
    retry: false,
    staleTime: Infinity,
  });

  const accept = useMutation({
    mutationFn: () => authClient.acceptInvitation({ token }),
    onSuccess: async (result) => {
      if ("token" in result) {
        // Session was minted immediately (existing user). The AuthClient
        // already captured `token` into localStorage; just refresh /me so
        // <RequireAuth> sees the new principal atomically with the nav.
        await auth.refresh();
        navigate(`/w/${result.workspace.slug}`);
        return;
      }
      // pending=true → magic link sent to confirm email; show check-email.
      const email = preview.data?.email ?? "";
      navigate(`/check-email?to=${encodeURIComponent(email)}`);
    },
    onError: (err: AuthClientError) => {
      toast.error(t("join.failed"), { description: formatApiError(err) });
    },
  });

  if (!token) {
    return (
      <AuthLayout title={t("join.title")}>
        <Alert variant="destructive" data-testid="join-missing-token">
          <AlertDescription>{t("join.missingToken")}</AlertDescription>
        </Alert>
      </AuthLayout>
    );
  }

  if (preview.isPending) {
    return (
      <AuthLayout title={t("join.title")}>
        <div
          className="flex justify-center py-6"
          data-testid="join-loading"
          aria-label={t("join.loading")}
        >
          <Spinner />
        </div>
      </AuthLayout>
    );
  }

  if (preview.error) {
    return (
      <AuthLayout title={t("join.title")}>
        <Alert variant="destructive" data-testid="join-invalid">
          <AlertDescription>{formatApiError(preview.error)}</AlertDescription>
        </Alert>
      </AuthLayout>
    );
  }

  const data = preview.data;
  if (!data) {
    return null;
  }

  return (
    <AuthLayout title={t("join.title")}>
      <div className="space-y-4" data-testid="join-body">
        <p className="text-sm">
          {t("join.invitation", {
            inviter: data.inviterEmail,
            workspace: data.workspace.name,
            role:
              data.role === "admin"
                ? t("join.roles.admin")
                : data.role === "member"
                  ? t("join.roles.member")
                  : data.role.charAt(0).toUpperCase() + data.role.slice(1).replace(/_/g, " "),
          })}
        </p>
        <Button
          type="button"
          className="h-10 w-full rounded-xl hover:bg-primary-hover"
          disabled={accept.isPending}
          onClick={() => accept.mutate()}
          data-testid="join-accept"
        >
          {accept.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              {t("join.accepting")}
            </>
          ) : (
            t("join.accept")
          )}
        </Button>
      </div>
    </AuthLayout>
  );
}
